/**
 * Handles file selection metadata for the PDP special request contact form.
 * Shopify contact forms do not attach binary files; selected file names are
 * copied into a hidden contact field for the merchant notification email.
 */
class PdpSpecialForm extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #fileInput = null;

  /** @type {HTMLInputElement | null} */
  #attachmentsField = null;

  /** @type {HTMLElement | null} */
  #fileList = null;

  /** @type {HTMLElement | null} */
  #fileError = null;

  connectedCallback() {
    this.#fileInput = this.querySelector('[data-pdp-file-input]');
    this.#attachmentsField = this.querySelector('[data-pdp-attachments-field]');
    this.#fileList = this.querySelector('[data-pdp-file-list]');
    this.#fileError = this.querySelector('[data-pdp-file-error]');

    if (!this.#fileInput || !this.#attachmentsField) return;

    this.#fileInput.addEventListener('change', () => this.#handleFileChange());
  }

  #handleFileChange() {
    if (!this.#fileInput || !this.#attachmentsField) return;

    const maxFiles = Number.parseInt(this.dataset.maxFiles || '5', 10);
    const maxSizeMb = Number.parseInt(this.dataset.maxFileSize || '10', 10);
    const maxSizeBytes = maxSizeMb * 1024 * 1024;
    const files = [...this.#fileInput.files];

    this.#clearFileError();

    if (files.length > maxFiles) {
      this.#showFileError(`Please select up to ${maxFiles} files.`);
      this.#fileInput.value = '';
      this.#updateAttachmentsField([]);
      return;
    }

    const oversized = files.find((file) => file.size > maxSizeBytes);
    if (oversized) {
      this.#showFileError(`Each file must be ${maxSizeMb}MB or smaller.`);
      this.#fileInput.value = '';
      this.#updateAttachmentsField([]);
      return;
    }

    this.#updateAttachmentsField(files);
  }

  /**
   * @param {File[]} files
   */
  #updateAttachmentsField(files) {
    if (!this.#attachmentsField || !this.#fileList) return;

    if (files.length === 0) {
      this.#attachmentsField.value = '';
      this.#fileList.textContent = '';
      this.#fileList.hidden = true;
      return;
    }

    const summary = files
      .map((file) => `${file.name} (${this.#formatFileSize(file.size)})`)
      .join(', ');

    this.#attachmentsField.value = summary;
    this.#fileList.textContent = summary;
    this.#fileList.hidden = false;
  }

  /**
   * @param {number} bytes
   * @returns {string}
   */
  #formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * @param {string} message
   */
  #showFileError(message) {
    if (!this.#fileError) return;
    this.#fileError.textContent = message;
    this.#fileError.hidden = false;
  }

  #clearFileError() {
    if (!this.#fileError) return;
    this.#fileError.textContent = '';
    this.#fileError.hidden = true;
  }
}

if (!customElements.get('pdp-special-form')) {
  customElements.define('pdp-special-form', PdpSpecialForm);
}
